import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  Paper,
  Theme,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import { Alert } from "@mui/material";
import DirtyFormSpy from "components/DirtyFormSpy";
import Field from "components/Field";
import FieldRichText from "components/FieldRichText";
import SelectProfession from "components/SelectProfession";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTrashablePromises from "hooks/useTrashablePromises";
import React, { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  SkuMajorTopic,
  SkuMajorTopicRequest,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import { SkuFormProps } from "types/sku-related-types";
import translateError from "utils/translateError";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  paper: {
    marginTop: "10px",
  },
}));

type MajorTopicEditProps = {
  onClose: () => void;
  skuMajorTopic?: SkuMajorTopic | undefined;
};

type SkuMajorTopicFormValues = {
  id: string | null;
  professionId: string;
  majorTopicDescription: string;
  majorTopicId: string | null;
};

const SkuMajorTopicForm = ({
  sku,
  skuMajorTopic,
  onClose,
  onClean,
  onDirty,
  onSkuUpdated,
}: MajorTopicEditProps & SkuFormProps) => {
  const { registerPromise } = useTrashablePromises();
  const queryClient = useQueryClient();
  const { skuService } = useGlobalContext();
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const init = useMemo<SkuMajorTopicFormValues>(
    () => ({
      id: skuMajorTopic?.id! ?? null,
      majorTopicId: skuMajorTopic?.majorTopic?.id! ?? null,
      majorTopicDescription: skuMajorTopic?.majorTopic?.description! ?? "",
      professionId: skuMajorTopic?.professionId! ?? "",
    }),
    [skuMajorTopic]
  );

  const form = useMemo(() => {
    const onSubmit = async (values: SkuMajorTopicFormValues) => {
      try {
        const response = await registerPromise(
          skuService.saveSkuMajorTopic(
            sku.id!,
            SkuMajorTopicRequest.fromJS(values)
          )
        );

        queryClient.invalidateQueries(["skuMajorTopics", sku.id!]);

        setSnackbarMessage(
          new SnackbarMessageProps(t({ id: "skuMajorTopicCreated" }))
        );

        onSkuUpdated && onSkuUpdated(sku.id!);
        onClose();

        return response;
      } catch (err: any) {
        console.log(err);
        return { [FORM_ERROR]: translateError(err, t) };
      }
    };

    return (
      <Form
        onSubmit={onSubmit}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError }) => (
          <form onSubmit={handleSubmit}>
            <DirtyFormSpy onDirty={onDirty} onClean={onClean} />
            <DialogTitle id="form-dialog-title">
              {t({ id: "skuMajorTopicTitle" })}
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={6}>
                <Field name="professionId">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <Tooltip
                        title={t({ id: "majorTopicEmptyProfessionMessage" })}
                      >
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectProfession
                            label={t({
                              id: "professionLabel",
                            })}
                            {...input}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Tooltip>
                    </Grid>
                  )}
                </Field>

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <Paper elevation={3} square sx={styles.paper}>
                    <Box
                      p={1}
                      display="flex"
                      alignItems="center"
                      data-cy="majorTopicPlaceHolderText"
                    >
                      <Field name="majorTopicDescription" required>
                        {({ input, meta }) => (
                          <FieldRichText
                            input={input}
                            meta={meta}
                            label={t({ id: "majorTopicPlaceholder" })}
                          />
                        )}
                      </Field>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              {submitting && <CircularProgress size={16} />}
              <Button
                disabled={submitting}
                onClick={onClose}
                data-cy="cancelButton"
              >
                {t({ id: "cancelButton" })}
              </Button>

              <Button
                type="submit"
                color="primary"
                disabled={submitting}
                data-cy="submitButton"
              >
                {t({ id: "skuMajorTopicCreateButton" })}
              </Button>
            </DialogActions>
            <SnackbarMessage
              snackbarMessage={snackbarMessage}
              setSnackbarMessage={setSnackbarMessage}
            />
          </form>
        )}
      />
    );
  }, [
    styles.feedback,
    styles.paper,
    init,
    onClean,
    onClose,
    onDirty,
    onSkuUpdated,
    queryClient,
    registerPromise,
    sku.id,
    skuService,
    snackbarMessage,
    t,
  ]);

  return form;
};
export default SkuMajorTopicForm;
